import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { FiX, FiCheck, FiZoomIn, FiZoomOut, FiMove } from 'react-icons/fi';

const ImageCropper = ({ image, onCropComplete, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropChange = (crop) => {
    setCrop(crop);
  };

  const onZoomChange = (zoom) => {
    setZoom(zoom);
  };

  const onCropCompleteHandler = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createCroppedImage = async () => {
    try {
      const croppedImage = await getCroppedImg(image, croppedAreaPixels);
      onCropComplete(croppedImage);
    } catch (e) {
      console.error('Error cropping image:', e);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
      {/* Header */}
      <div className="bg-black/50 px-4 py-3 flex items-center justify-between">
        <button
          onClick={onCancel}
          className="p-2 text-white hover:bg-white/10 rounded-full"
        >
          <FiX size={24} />
        </button>
        <h2 className="text-white font-semibold">Ajustar Logo</h2>
        <button
          onClick={createCroppedImage}
          className="p-2 text-green-400 hover:bg-white/10 rounded-full"
        >
          <FiCheck size={24} />
        </button>
      </div>

      {/* Instructions */}
      <div className="bg-amber-500 px-4 py-2 text-center">
        <p className="text-black text-sm font-medium flex items-center justify-center gap-2">
          <FiMove /> Arraste para centralizar o logo
        </p>
      </div>

      {/* Cropper Area */}
      <div className="flex-1 relative">
        <Cropper
          image={image}
          crop={crop}
          zoom={zoom}
          aspect={1}
          cropShape="round"
          showGrid={false}
          onCropChange={onCropChange}
          onZoomChange={onZoomChange}
          onCropComplete={onCropCompleteHandler}
        />
      </div>

      {/* Zoom Controls */}
      <div className="bg-black/50 px-4 py-4">
        <div className="flex items-center gap-4 max-w-xs mx-auto">
          <FiZoomOut className="text-white" />
          <input
            type="range"
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 h-2 bg-white/30 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full"
          />
          <FiZoomIn className="text-white" />
        </div>
        <p className="text-white/60 text-xs text-center mt-2">
          Use o slider para dar zoom na imagem
        </p>
      </div>

      {/* Action Buttons */}
      <div className="bg-black px-4 py-4 flex gap-3">
        <button
          onClick={onCancel}
          className="flex-1 py-3 bg-gray-700 text-white rounded-xl font-semibold hover:bg-gray-600"
        >
          Cancelar
        </button>
        <button
          onClick={createCroppedImage}
          className="flex-1 py-3 bg-[#DC143C] text-white rounded-xl font-semibold hover:bg-[#B01030]"
        >
          Confirmar
        </button>
      </div>
    </div>
  );
};

// Helper function to create cropped image
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Set canvas size to desired output (200x200 for logo)
  const size = 200;
  canvas.width = size;
  canvas.height = size;

  // Draw the cropped image
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    size,
    size
  );

  // Return as blob/file
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.9);
  });
};

export default ImageCropper;
